/**
 * useDisplayPreferences — read, apply, and persist display preferences.
 *
 * Applies data-* attributes to <html> on change (via ThemeProvider's effect).
 * Persists to localStorage always. Server persistence is a surface concern:
 * ThemeProvider invokes a surface-supplied `onPersist` callback — shared code
 * here never knows the endpoint and never fetches.
 *
 * theme=system: attaches a prefers-color-scheme media-query listener and
 * resolves live; removes the listener when switching away.
 *
 * Consumed via ThemeProvider — call `useDisplayPreferences()` inside the
 * ThemeProvider tree.
 */
import { useContext } from 'react'
import { DisplayPreferencesContext, type DisplayPreferencesContextValue } from './ThemeProvider'

export type { DisplayPreferencesContextValue }

/**
 * Returns the current display preferences and mutators.
 * Must be called inside a <ThemeProvider> tree.
 */
export function useDisplayPreferences(): DisplayPreferencesContextValue {
  const ctx = useContext(DisplayPreferencesContext)
  if (!ctx) {
    throw new Error('useDisplayPreferences must be used inside <ThemeProvider>')
  }
  return ctx
}
